import React from 'react';
import { Button } from 'antd';
import { TProspectInsurance } from 'types/common';
import { ELIGIBILITY_PRICE_CENTS, TBackendEligibilityCheckVo } from 'types/insuranceEligibility';
import { ELIGIBILITY_STATUS_CONFIG } from 'components/EligibilityStatusTag';
import { deriveEligibilityView } from 'utils/clientEligibility';
import { adaptCheckVo, isUnmappedStatus, withZone } from 'api/eligibility/adapt';
import { INSURANCE_GROUP_COPY, OTHER_PRIMARY_COPY } from '../copy';
import { formatCents, formatCheckedDate, isEligibleResult } from '../derive';
import s from '../s.module.less';

type Props = {
    // 档案上除结果卡那张以外的 primary（default 在前）
    insurances: TProspectInsurance[];
    // 10/21：除结果卡那张以外的次保险，随时可选（手动查由 provider 定顺序）
    secondaries: TProspectInsurance[];
    // 每张卡最后一次 check：报上次答案；本轮已失败的置灰，免得同一个问题付两次钱
    checkCards: Map<number, TBackendEligibilityCheckVo>;
    selectedId?: number;
    onSelect: (insuranceId: number) => void;
    onCheck: (insuranceId: number) => void;
};

// 10/05 失败后「查另一张」（仅 not enrolled；原型 otherOffer）。一个自带按钮的盒子而不是一个 "Or" 链接：
// 患者自己档案上的另一张，排在 $1.50 的目录搜索之前，要长得像「下一步」。一次只查一张，不自动跑——
// 每张 $0.25，provider 可能已经知道答案
const OtherPrimaryOffer: React.FC<Props> = ({ insurances, secondaries, checkCards, selectedId, onSelect, onCheck }) => {
    const all = [...insurances, ...secondaries];
    const n = all.length;
    // 「本轮」= 最近一次 Eligible 之后。本轮里查过且没成功的置灰（不是 Eligible 都算失败，UNMAPPED 也算）；
    // 上次 Eligible 的、本轮之前失败的都还能选——患者可能已经改过那张卡
    const cards = Array.from(checkCards.values());
    const timeOf = (vo: TBackendEligibilityCheckVo) =>
        new Date(withZone(vo.latestCheckedAt || undefined) || 0).getTime();
    const lastEligibleAt = cards.reduce<number | undefined>((acc, vo) => {
        const adapted = adaptCheckVo(vo);
        if (!adapted || !isEligibleResult(adapted) || !vo.latestCheckedAt) return acc;
        const at = timeOf(vo);
        return acc == null || at > acc ? at : acc;
    }, undefined);
    const spentThisRound = (id?: number) => {
        const vo = id != null ? checkCards.get(id) : undefined;
        if (!vo?.latestCheckedAt) return false;
        const adapted = adaptCheckVo(vo);
        const failed = isUnmappedStatus(vo) || (!!adapted && !isEligibleResult(adapted));
        if (!failed) return false;
        return lastEligibleAt == null || timeOf(vo) > lastEligibleAt;
    };
    const left = all.filter((ins) => ins.id != null && !spentThisRound(ins.id));
    // 只剩一张没查过时预选它；用户点过的那张（还没查）优先
    let picked: number | undefined;
    if (selectedId != null && left.some((ins) => ins.id === selectedId)) picked = selectedId;
    else if (left.length === 1) picked = left[0].id;
    // 预选要回写到 hook：precheck 与发出前的闸（template / payer ID / NPI）按「下一次发往的那张」算，
    // 只在组件里亮着的话，点 Check 会发给这张、却按结果卡那张的 precheck 放行或拦截。
    // 不带依赖数组：详情重拉时 hook 会在同一次 commit 里（子 effect 之后）把选择清空，回写被吞掉，
    // 而 picked/selectedId 与上一轮相同、带依赖的 effect 不会再跑，就一直停在结果卡那张。每轮都比一次，有守卫不会循环
    React.useEffect(() => {
        if (picked != null && picked !== selectedId) onSelect(picked);
    });
    if (!n) return null;

    let tail: string = OTHER_PRIMARY_COPY.tailNone;
    if (left.length) tail = n === 1 ? OTHER_PRIMARY_COPY.tailOne : OTHER_PRIMARY_COPY.tailMany;

    return (
        <div className={`${s.fixBox} ${s.otherOffer}`}>
            {/* "insurance" 而不是 "primary insurance"：provider 在决定下一张查什么，不是它在哪个槽位 */}
            <b>{n === 1 ? OTHER_PRIMARY_COPY.leadOne : OTHER_PRIMARY_COPY.leadMany(n)}</b>
            {tail}
            {/* 10/21：Primary / Secondary 分组；一律带组标签，告诉 provider 每张是哪种保险 */}
            {[
                { key: 'primary', label: INSURANCE_GROUP_COPY.primary, list: insurances },
                { key: 'secondary', label: INSURANCE_GROUP_COPY.secondary, list: secondaries },
            ]
                .filter((g) => g.list.length > 0)
                .map((g) => (
                    <React.Fragment key={g.key}>
                        <span className={s.insGroupLabel}>{g.label}</span>
                        <div className={s.radioCards} role="radiogroup">
                            {g.list.map((ins) => {
                                const tried = ins.id != null ? checkCards.get(ins.id) : undefined;
                                const spent = spentThisRound(ins.id);
                                const on = ins.id === picked;
                                const label = tried
                                    ? ELIGIBILITY_STATUS_CONFIG[deriveEligibilityView(tried, true).status]?.label
                                    : undefined;
                                return (
                                    <button
                                        key={ins.id}
                                        type="button"
                                        role="radio"
                                        aria-checked={on}
                                        disabled={spent}
                                        className={`${s.radioCard} ${on ? s.radioCardOn : ''} ${spent ? s.radioCardDone : ''}`}
                                        onClick={() => ins.id != null && onSelect(ins.id)}
                                    >
                                        <span className={s.radioDot} />
                                        <span>
                                            <span className={s.radioTitle}>
                                                {OTHER_PRIMARY_COPY.title(ins.carrier, ins.plan)}
                                            </span>
                                            <span className={s.radioDesc}>
                                                {[
                                                    ins.memberId ? `Member ID ${ins.memberId}` : '',
                                                    ins.groupId ? `Group ${ins.groupId}` : '',
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </span>
                                            {/* 上次答案放在 ID 下面而不是右侧标签：标签会把 plan 名挤成三行 */}
                                            {tried && label && tried.latestCheckedAt && (
                                                <span className={s.otherTried}>
                                                    {OTHER_PRIMARY_COPY.tried(
                                                        label,
                                                        formatCheckedDate(tried.latestCheckedAt),
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </React.Fragment>
                ))}
            {left.length > 0 && (
                <div className={s.fixActions}>
                    <Button disabled={picked == null} onClick={() => picked != null && onCheck(picked)}>
                        {picked == null
                            ? OTHER_PRIMARY_COPY.selectCta
                            : OTHER_PRIMARY_COPY.checkCta(formatCents(ELIGIBILITY_PRICE_CENTS))}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default OtherPrimaryOffer;
